// client/src/App.jsx
import { useState, useEffect, useMemo } from 'react';
import '../App.css';
import Select from 'react-select';
import axios from 'axios';
import { RRule } from 'rrule';
import { Time } from 'rrule/dist/esm/datetime';
import { Temporal } from '@js-temporal/polyfill';
import { formatDatePretty, formatTimeShort, formatMs } from '../utils/dates';
function todo({ user, onLogout }) {
  const [todos, setTodos] = useState([]);
  const [occurrenceDoneRows, setOccurrenceDoneRows] = useState([]);
  const [task, setTask] = useState('');
  const [date_time, setdate_time] = useState('');
  const [time, set_time] = useState('');
  const [timeAware, setTimeAware] = useState(true);
  const [currentDate, setCurrentDate] = useState(() => Temporal.Now.plainDateISO());
  // store `date_to_see` as milliseconds (UTC midnight) to avoid timezone issues
  const [date_to_see, set_date_to_see] = useState(() => {
    const today = Temporal.Now.plainDateISO();
    const utcMidnightMs = Temporal.ZonedDateTime.from({
      year: today.year,
      month: today.month,
      day: today.day,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).toInstant().epochMilliseconds;
    return utcMidnightMs;
  });
  // helper to normalize date representations to milliseconds
  const toMs = (v) => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string' && v.includes('/')) {
      const [month, day, year] = v.split('/').map((s) => parseInt(s));
      const pd = Temporal.PlainDate.from({ year, month, day });
      return Temporal.ZonedDateTime.from({
        year: pd.year,
        month: pd.month,
        day: pd.day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).toInstant().epochMilliseconds;
    }
    const s = String(v).trim();
    if (/^\d+$/.test(s)) return Number(s);
    try {
      return Temporal.Instant.from(s).epochMilliseconds;
    } catch (e1) {
      try {
        const pdt = Temporal.PlainDateTime.from(s);
        const z = Temporal.ZonedDateTime.from({
          year: pdt.year,
          month: pdt.month,
          day: pdt.day,
          hour: pdt.hour,
          minute: pdt.minute,
          second: pdt.second,
          millisecond: pdt.millisecond || 0,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        return z.toInstant().epochMilliseconds;
      } catch (e2) {
        try {
          const pd = Temporal.PlainDate.from(s);
          const z = Temporal.ZonedDateTime.from({
            year: pd.year,
            month: pd.month,
            day: pd.day,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });
          return z.toInstant().epochMilliseconds;
        } catch (e3) {
          return NaN;
        }
      }
    }
  };
  // Helper to convert epoch-ms to JS Date for third-party interop (rrule)
  const msToJSDate = (ms) => new Date(Number(ms));
  const [recurrence, setRecurrence] = useState('');

  // Calendar constants
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const fetchTodos = async () => {
    const [todosRes, occurrenceRes] = await Promise.all([
      axios.get('/api/todos'),
      axios.get('/api/todooccurrences'),
    ]);

    setTodos(todosRes.data);
    setOccurrenceDoneRows(occurrenceRes.data);
  };

  const addTodo = async () => {
    if (!task) return;
    if (!date_time) return;
    const [year, month, day] = date_time.value.split('-');
    const [hour, minute] = time.value ? time.value.split(':') : [0, 0];

    const utcMs = Temporal.ZonedDateTime.from({
      year: parseInt(year),
      month: parseInt(month),
      day: parseInt(day),
      hour: parseInt(hour),
      minute: parseInt(minute),
      second: 0,
      millisecond: 0,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).toInstant().epochMilliseconds;

    const today = Temporal.Now.plainDateISO();
    const selectedDate = Temporal.PlainDate.from({
      year: parseInt(year),
      month: parseInt(month),
      day: parseInt(day),
    });

    await axios.post('/api/todos', {
      user: user.data.username,
      task,
      date_time: utcMs,
      time: time.value,
      recurrence: recurrence.value,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      time_aware: timeAware,
    });

    if (Temporal.PlainDate.compare(today, selectedDate) === 0) {
      await axios.post('/api/dailyreport', {
        user: user.data.username,
      });
    }

    setTask('');
    setdate_time(''); // Clear the date selection after adding
    set_time('');
    setRecurrence('');
    fetchTodos();
  };

  const expandedTodos = useMemo(() => {
    // Build expanded list mapping recurring rules once per todos change
    const sixMonthsFromNowPlain = Temporal.Now.plainDateISO().add({ months: 6 });
    const endEpoch = Temporal.ZonedDateTime.from({
      year: sixMonthsFromNowPlain.year,
      month: sixMonthsFromNowPlain.month,
      day: sixMonthsFromNowPlain.day,
      hour: 12,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).toInstant().epochMilliseconds;
    const endJs = msToJSDate(endEpoch);
    const completedOccurrenceKeys = new Set(
      occurrenceDoneRows.map((row) => `${row.todo_id}|${Number(row.occurrence_ms)}`)
    );
    const occurrenceTodoIds = new Set(occurrenceDoneRows.map((row) => Number(row.todo_id)));

    return todos.flatMap((todo) => {
      if (todo.user !== user.data.username) return [];

      const todoMs = typeof todo.date_time === 'number' ? todo.date_time : toMs(todo.date_time);
      if (!todoMs) return [];

      const todoZdt = Temporal.Instant.fromEpochMilliseconds(Number(todoMs)).toZonedDateTimeISO(
        todo.time_aware ? Intl.DateTimeFormat().resolvedOptions().timeZone : todo.timezone
      );
      const dtstartNoon = msToJSDate(
        Temporal.ZonedDateTime.from({
          year: todoZdt.year,
          month: todoZdt.month,
          day: todoZdt.day,
          hour: 12,
          minute: 0,
          second: 0,
          millisecond: 0,
          timeZone: todo.time_aware
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : todo.timezone,
        }).toInstant().epochMilliseconds
      );

      let rule = null;
      if (todo.recurring === 'daily')
        rule = new RRule({ freq: RRule.DAILY, interval: 1, dtstart: dtstartNoon });
      else if (todo.recurring === 'weekly')
        rule = new RRule({ freq: RRule.WEEKLY, interval: 1, dtstart: dtstartNoon });
      else if (todo.recurring === 'biweekly')
        rule = new RRule({ freq: RRule.WEEKLY, interval: 2, dtstart: dtstartNoon });
      else if (todo.recurring === 'monthly')
        rule = new RRule({ freq: RRule.MONTHLY, interval: 1, dtstart: dtstartNoon });

      if (rule) {
        const startJs = dtstartNoon;
        const occurrences = rule.between(startJs, endJs, true);
        return occurrences.map((date) => {
          const matchedDate = date.getTime();
          const occurrenceKey = `${todo.id}|${matchedDate}`;
          const hasOccurrenceRecords = occurrenceTodoIds.has(Number(todo.id));
          const occurrenceDone =
            completedOccurrenceKeys.has(occurrenceKey) || (!hasOccurrenceRecords && !!todo.done);

          return {
            ...todo,
            matchedDate,
            occurrenceDone,
          };
        });
      }

      return [
        {
          ...todo,
          matchedDate: todoMs,
          occurrenceDone: !!todo.done,
        },
      ];
    });
  }, [todos, user, occurrenceDoneRows]);

  const deleteTodo = async (id) => {
    await axios.delete(`/api/todos?id=${id}`);
    fetchTodos();
  };

  const doneTodo = async (id, matchedDate, recurring) => {
    if (recurring && matchedDate) {
      await axios.put(`/api/todos?id=${id}&occurrenceMs=${matchedDate}`);
    } else {
      await axios.put(`/api/todos?id=${id}`);
    }
    fetchTodos();
  };

  const resetDay = () => {
    const today = Temporal.Now.plainDateISO();
    const utcMidnightMs = Temporal.ZonedDateTime.from({
      year: today.year,
      month: today.month,
      day: today.day,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).toInstant().epochMilliseconds;
    set_date_to_see(utcMidnightMs);
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      height: '44px',
      width: '100%',
      background: 'var(--surface)',
      borderColor: state.isFocused ? 'var(--brand)' : 'var(--border)',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.18)' : 'none',
      '&:hover': {
        borderColor: 'var(--brand)',
      },
      fontSize: '0.95rem',
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      borderRadius: '14px',
      backgroundClip: 'padding-box',
      overflow: 'hidden',
      border: '1px solid var(--border)',
    }),

    valueContainer: (provided) => ({
      ...provided,
      height: '44px',
      fontSize: '0.95rem',
      padding: '0 0.85rem',
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    }),

    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
      color: 'var(--panel-text) !important',
      fontSize: '0.95rem',
      lineHeight: '44px',
      height: '100%',
    }),

    indicatorSeparator: (provided) => ({
      ...provided,
    }),

    indicatorsContainer: (provided) => ({
      ...provided,
      height: '44px',
      display: 'flex',
      alignItems: 'center',
    }),

    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '0 10px',
      display: 'flex',
      alignItems: 'center',
    }),

    clearIndicator: (provided) => ({
      ...provided,
      padding: '0 4px',
    }),

    singleValue: (provided) => ({
      ...provided,
      color: 'var(--panel-text) !important',
      fontSize: '0.95rem',
      lineHeight: '44px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'block',
    }),

    placeholder: (provided) => ({
      ...provided,
      color: 'var(--panel-muted) !important',
      fontSize: '0.95rem',
      lineHeight: '44px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'block',
    }),

    menu: (provided) => ({
      ...provided,
      background: 'var(--surface-strong) !important',
      border: '1px solid var(--border)',
      boxShadow: '0 16px 32px rgba(33, 18, 69, 0.18)',
      width: '100%',
      fontSize: '0.95rem',
      borderRadius: '14px',
      backgroundClip: 'padding-box',
      zIndex: 9999,
      overflow: 'hidden',
    }),

    menuList: (provided) => ({
      ...provided,
      background: 'var(--surface-strong) !important',
      backgroundColor: 'var(--surface-strong) !important',
      color: 'var(--panel-text) !important',
      borderRadius: '14px',
      backgroundClip: 'padding-box',
      width: '100%',
      fontSize: '0.85rem',
      maxHeight: '180px',
      overflowY: 'auto',
      padding: '0 0 4px 0',
    }),

    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),

    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'rgba(102, 126, 234, 0.12)' : 'transparent',
      color: 'var(--panel-text) !important',
      fontSize: '0.95rem',
      width: '100%',
      padding: '8px 12px',
      '&:hover': {
        backgroundColor: 'rgba(102, 126, 234, 0.16)',
      },
    }),
  };

  useEffect(() => {
    fetchTodos();
  }, []);

  useEffect(() => {
    if (!date_time?.value || !time) return;

    const selectedDate = Temporal.PlainDate.from(date_time.value);
    const today = Temporal.Now.plainDateISO();

    const isToday = Temporal.PlainDate.compare(today, selectedDate) === 0;

    if (!isToday) return;

    const now = Temporal.Now.zonedDateTimeISO(Intl.DateTimeFormat().resolvedOptions().timeZone);

    const currentMinutes = now.hour * 60 + now.minute;

    const [hour, minute] = time.value.split(':').map(Number);
    const selectedMinutes = hour * 60 + minute;

    if (selectedMinutes < currentMinutes) {
      set_time(''); // or default like "All Day" or next valid slot
    }
  }, [date_time, time]);

  useEffect(() => {
    const userTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const today = Temporal.Now.plainDateISO(userTZ);
    const tomorrow = today.add({ days: 1 });

    const midnightMs = Temporal.ZonedDateTime.from({
      year: tomorrow.year,
      month: tomorrow.month,
      day: tomorrow.day,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
      timeZone: userTZ, // ← user's timezone
    }).toInstant().epochMilliseconds;

    const now = Temporal.Now.instant().epochMilliseconds;
    const delay = midnightMs - now;

    const timeout = setTimeout(() => {
      setCurrentDate(Temporal.Now.plainDateISO(userTZ));
    }, delay);

    return () => clearTimeout(timeout);
  }, [currentDate]);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    // `date` is a Temporal.PlainDate
    return date.daysInMonth;
  };

  const getFirstDayOfMonth = (date) => {
    // Temporal.PlainDate.dayOfWeek: 1=Monday..7=Sunday. Convert to JS day index 0=Sunday..6=Saturday
    const pd = Temporal.PlainDate.from({ year: date.year, month: date.month, day: 1 });
    return pd.dayOfWeek % 7;
  };

  const changeMonth = (direction) => {
    setCurrentDate((prev) => prev.add({ months: direction }));
  };

  const getDates = () => {
    const dates = [];
    let today = Temporal.Now.plainDateISO();

    for (let i = 0; i < 30; i++) {
      const date = today.add({ days: i });
      dates.push(date);
    }
    return dates;
  };

  const generateTimeOptions = (
    startHour = 0,
    endHour = 23,
    intervalMinutes = 30,
    format12Hour = false
  ) => {
    const times = [];

    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += intervalMinutes) {
        if (hour === endHour && minute >= 0) break;

        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

        if (format12Hour) {
          const h12 = hour % 12 === 0 ? 12 : hour % 12;
          const ampm = hour < 12 ? 'AM' : 'PM';
          const minuteStr = minute.toString().padStart(2, '0');
          const time12 = `${h12}:${minuteStr} ${ampm}`;
          times.push({ value: time24, display: time12 });
        } else {
          times.push({ value: time24, display: time24 });
        }
      }
    }
    return times;
  };

  const dateOptions = getDates().map((date) => ({
    value: date.toString(),
    label: date.toString(),
  }));

  const today = Temporal.Now.plainDateISO();

  let selectedDate = null;

  if (!date_time?.value) {
    selectedDate = Temporal.PlainDate.from({
      year: today.year,
      month: today.month,
      day: today.day,
    });
  } else {
    selectedDate = Temporal.PlainDate.from(date_time.value);
  }

  const isToday = Temporal.PlainDate.compare(today, selectedDate) === 0;

  const now = Temporal.Now.zonedDateTimeISO(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const currentMinutes = now.hour * 60 + now.minute;

  const timeOptions = [
    { value: '', label: 'All Day' },
    ...generateTimeOptions(0, 24, 15, true)
      .filter((time) => {
        if (!isToday) return true;

        const [hour, minute] = time.value.split(':').map(Number);
        return hour * 60 + minute >= currentMinutes;
      })
      .map((time) => ({
        value: time.value,
        label: time.display,
      })),
  ];

  const recurringOptions = [
    { value: '', label: 'None' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Biweekly' },
    { value: 'monthly', label: 'Monthly' },
  ];

  return (
    <div className="todo-layout">
      <div className="todo-layout__main todo-board">
        <div className="todo-board__header">
          <div className="todo-board__heading">
            <span className="todo-board__eyebrow">Task board</span>
            <h1 className="todo-board__title">To-Do List</h1>
            <p className="todo-board__copy">
              Quickly add tasks, pick a date, and keep recurring work in view.
            </p>
          </div>

          {(() => {
            const today = Temporal.Now.plainDateISO();
            const utcMidnightMs = Temporal.ZonedDateTime.from({
              year: today.year,
              month: today.month,
              day: today.day,
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }).toInstant().epochMilliseconds;
            const isToday = date_to_see !== utcMidnightMs;
            return (
              isToday && (
                <button onClick={resetDay} className="todo-board__reset">
                  Reset filter
                </button>
              )
            );
          })()}
        </div>

        {/* Form Section - Takes up remaining space but allows scrolling */}
        <div className="todo-add-card">
          <div className="todo-add-card__header">
            <span className="todo-add-card__eyebrow">Quick add</span>
            <h2 className="todo-add-card__title">Add a new to-do</h2>
            <p className="todo-add-card__copy">
              Drop in a task, pick a date, and decide whether it repeats.
            </p>
          </div>

          <div className="todo-add-card__fields">
            {/* Task Input */}
            <div className="todo-add-card__field">
              <input
                value={task}
                onChange={(e) => setTask(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addTodo();
                  }
                }}
                className="todo-add-card__input"
                placeholder="Enter your task..."
              />
            </div>

            {/* Date Select */}
            <div className="todo-add-card__field">
              <Select
                onChange={setdate_time}
                value={date_time}
                styles={customSelectStyles}
                options={dateOptions}
                classNamePrefix="custom-select"
                placeholder="Select the due date..."
                isClearable={true}
                isSearchable={false}
                maxMenuHeight={180}
                menuShouldScrollIntoView={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                captureMenuScroll={true}
                menuShouldBlockScroll={true}
              />
            </div>

            {/* Time Select */}
            <div className="todo-add-card__field">
              <Select
                onChange={set_time}
                value={time}
                styles={customSelectStyles}
                options={timeOptions}
                classNamePrefix="custom-select"
                placeholder="Time / All Day"
                isClearable={true}
                isSearchable={false}
                maxMenuHeight={180}
                menuShouldScrollIntoView={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                captureMenuScroll={true}
                menuShouldBlockScroll={true}
              />
            </div>

            {/* Recurrence Select */}
            <div className="todo-add-card__field">
              <Select
                onChange={setRecurrence}
                value={recurrence}
                styles={customSelectStyles}
                options={recurringOptions}
                classNamePrefix="custom-select"
                placeholder="Select the recurrence..."
                isClearable={true}
                isSearchable={false}
                maxMenuHeight={180}
                menuShouldScrollIntoView={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                captureMenuScroll={true}
                menuShouldBlockScroll={true}
              />
            </div>
          </div>

          <div>
            <input
              type="checkbox"
              checked={timeAware}
              onChange={(e) => setTimeAware(e.target.checked)}
            />
            <label htmlFor="time-aware">Timezone-Aware</label>
          </div>

          {/* Action Button */}
          <div className="todo-add-card__actions">
            <button onClick={addTodo} className="todo-add-card__submit">
              Add a To-Do
            </button>
          </div>
        </div>
      </div>

      {/* Row 2 */}
      <div
        className="todo-layout__side"
        style={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          flexDirection: 'column',
          height: '100%',
          minHeight: '100%',
          alignSelf: 'stretch',
          borderRadius: '18px',
          paddingLeft: '1.25rem',
          gap: '2rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            width: '100%',
            flex: '0 0 auto',
            borderRadius: '18px',
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--surface-strong)',
              borderRadius: '18px',
              padding: '0.75rem',
              paddingBottom: '0.8rem',
              boxShadow: '0 12px 28px rgba(33, 18, 69, 0.10)',
              width: '100%',
              height: 'auto',
              alignSelf: 'stretch',
              marginBottom: '0',
            }}
          >
            {/* Calendar Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.3rem',
              }}
            >
              <button
                onClick={() => changeMonth(-1)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  padding: '0.1rem 0.3rem',
                }}
              >
                &#8249;
              </button>
              <h3 style={{ margin: 0, fontSize: '0.96rem' }}>
                {monthNames[currentDate.month - 1]} {currentDate.year}
              </h3>
              <button
                onClick={() => changeMonth(1)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  padding: '0.1rem 0.3rem',
                }}
              >
                &#8250;
              </button>
            </div>

            {/* Day Headers */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '0.1rem',
                marginBottom: '0.25rem',
              }}
            >
              {dayNames.map((day) => (
                <div
                  key={day}
                  style={{
                    textAlign: 'center',
                    fontSize: '0.6rem',
                    fontWeight: 'bold',
                    color: 'var(--panel-muted)',
                    padding: '0.08rem 0',
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '0.08rem 0.08rem',
              }}
            >
              {/* Empty cells for days before the first day of the month */}
              {Array.from({ length: getFirstDayOfMonth(currentDate) }, (_, i) => (
                <div key={`empty-${i}`} style={{ height: '1.75rem' }}></div>
              ))}

              {/* Days of the month */}
              {Array.from({ length: getDaysInMonth(currentDate) }, (_, i) => {
                const day = i + 1;
                const today = Temporal.Now.zonedDateTimeISO()
                  .withTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
                  .toPlainDate();
                const isToday =
                  currentDate.year === today.year &&
                  currentDate.month === today.month &&
                  day === today.day;

                const targetPlain = Temporal.PlainDate.from({
                  year: currentDate.year,
                  month: currentDate.month,
                  day,
                });

                const targetMs = Temporal.ZonedDateTime.from({
                  year: targetPlain.year,
                  month: targetPlain.month,
                  day: targetPlain.day,
                  hour: 0,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                }).toInstant().epochMilliseconds;
                const isDateToSee = date_to_see && date_to_see === targetMs;

                const dotCount = expandedTodos.filter((todo) => {
                  if (todo.user !== user.data.username) return false;
                  if (!todo.matchedDate) return false;
                  const todoTZ = todo.time_aware
                    ? Intl.DateTimeFormat().resolvedOptions().timeZone
                    : todo.timezone;
                  const todoZ = Temporal.Instant.fromEpochMilliseconds(
                    Number(todo.matchedDate)
                  ).toZonedDateTimeISO(todoTZ);
                  const targetZ = Temporal.Instant.fromEpochMilliseconds(
                    targetMs
                  ).toZonedDateTimeISO(Intl.DateTimeFormat().resolvedOptions().timeZone);
                  return (
                    todoZ.year === targetZ.year &&
                    todoZ.month === targetZ.month &&
                    todoZ.day === targetZ.day
                  );
                }).length;

                const limitedDots = Math.min(dotCount, 3);
                return (
                  <div
                    key={day}
                    style={{
                      height: '100%',
                      minHeight: '2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: 'column',
                      fontSize: '0.8rem',
                      lineHeight: 1,
                      cursor: 'pointer',
                      padding: '0.06rem 0 0.08rem',
                      borderRadius: '4px',
                      // Combine all the background logic here
                      background: isDateToSee
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : isToday
                          ? '#667eea'
                          : 'transparent',
                      color: isDateToSee || isToday ? 'var(--on-brand)' : 'var(--panel-text)',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isToday && !isDateToSee) e.target.style.backgroundColor = '#f0f0f0';
                    }}
                    onMouseLeave={(e) => {
                      if (!isToday && !isDateToSee) e.target.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => {
                      const targetMs = Temporal.ZonedDateTime.from({
                        year: currentDate.year,
                        month: currentDate.month,
                        day,
                        hour: 0,
                        minute: 0,
                        second: 0,
                        millisecond: 0,
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                      }).toInstant().epochMilliseconds;
                      set_date_to_see(targetMs);
                    }}
                  >
                    <span style={{ display: 'block', transform: 'translateY(6px)' }}>{day}</span>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '1px',
                        marginTop: 'auto',
                        minHeight: '0.3rem',
                      }}
                    >
                      {Array.from({ length: limitedDots }).map((_, idx) => (
                        <span
                          key={idx}
                          style={{
                            width: '3px',
                            height: '3px',
                            borderRadius: '50%',
                            backgroundColor:
                              isDateToSee || isToday ? 'var(--on-brand)' : 'var(--panel-muted)',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Row 3 - Todo List */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minHeight: '0',
            flex: '1 1 auto',
            height: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              width: '100%',
              height: '100%',
              minHeight: '0',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                flex: '1 1 auto',
                minHeight: '0',
                backgroundColor: 'var(--surface-strong)',
                borderRadius: '18px',
                padding: '1.25rem',
                paddingTop: '1rem',
                overflow: 'hidden',
                boxShadow: '0 12px 28px rgba(33, 18, 69, 0.10)',
              }}
            >
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'auto',
                  height: '100%',
                  flexShrink: 1,
                }}
              >
                {expandedTodos
                  .filter((todo) => {
                    if (!user?.data.username) return false;
                    if (!todo.user) return false;

                    const userCorrect =
                      user.data.username.toLowerCase() === todo.user.toLowerCase();
                    if (!userCorrect) return false;

                    if (!todo.matchedDate) return false;
                    const todoZ = Temporal.Instant.fromEpochMilliseconds(
                      Number(todo.matchedDate)
                    ).toZonedDateTimeISO(
                      todo.time_aware
                        ? Intl.DateTimeFormat().resolvedOptions().timeZone
                        : todo.timezone
                    );
                    const selZ = Temporal.Instant.fromEpochMilliseconds(
                      Number(date_to_see)
                    ).toZonedDateTimeISO(Intl.DateTimeFormat().resolvedOptions().timeZone);
                    return (
                      todoZ.year === selZ.year &&
                      todoZ.month === selZ.month &&
                      todoZ.day === selZ.day
                    );
                  })
                  .sort((a, b) => {
                    if (!a.date_time && !b.date_time) return 0;
                    if (!a.date_time) return 1;
                    if (!b.date_time) return -1;

                    return toMs(a.date_time) - toMs(b.date_time);
                  })
                  .map((todo) => (
                    <li
                      key={`${todo.id}-${todo.matchedDate}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '2vh', // Reduced from 10%
                        height: 'auto',
                        marginBottom: '1vh', // Reduced from 10%
                        backgroundColor: 'var(--surface)',
                        borderRadius: '4px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        position: 'relative',
                        width: '100%', // Ensure full width usage
                        boxSizing: 'border-box', // Include padding in width calculation
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%', // Use full width
                          gap: '1vh', // Add spacing between elements
                        }}
                      >
                        <div
                          style={{
                            flex: '1', // Take up remaining space
                            minWidth: '0', // Allow shrinking
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5vh',
                          }}
                        >
                          <div
                            style={{
                              wordBreak: 'break-word', // Handle long text
                              overflow: 'hidden',
                            }}
                          >
                            <span style={{ fontWeight: '500' }}>{todo.task}</span>
                            {!!todo.occurrenceDone && (
                              <span style={{ color: '#28a745', marginLeft: '0.5rem' }}>(Done)</span>
                            )}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '0.5rem',
                              fontSize: '1.8vh',
                              color: 'var(--panel-muted)',
                            }}
                          >
                            {todo.matchedDate && (
                              <span>
                                {formatDatePretty(todo.matchedDate, {
                                  timeZone: todo.time_aware
                                    ? Intl.DateTimeFormat().resolvedOptions().timeZone
                                    : todo.timezone,
                                })}
                              </span>
                            )}
                            {todo.date_time && todo.time && (
                              <span>
                                (
                                {formatMs(todo.date_time, {
                                  options: { hour: 'numeric', minute: '2-digit' },
                                  timeZone: todo.time_aware
                                    ? Intl.DateTimeFormat().resolvedOptions().timeZone
                                    : todo.timezone,
                                })}
                                )
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: '0.5vh',
                            flexShrink: 0, // Prevent buttons from shrinking
                          }}
                        >
                          <button
                            onClick={() => doneTodo(todo.id, todo.matchedDate, !!todo.recurring)}
                            disabled={!!todo.occurrenceDone}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '2vh',
                              padding: '0.5vh',
                              minWidth: '3vh',
                              minHeight: '3vh',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: todo.occurrenceDone ? 0.55 : 1,
                            }}
                          >
                            ✅
                          </button>
                          <button
                            onClick={() => deleteTodo(todo.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '2vh',
                              padding: '0.5vh',
                              minWidth: '3vh',
                              minHeight: '3vh',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default todo;
